import React from 'react';
import {
  FaExternalLinkAlt,
  FaLock,
  FaBullseye,
  FaGlobe,
  FaEnvelope,
  FaTag,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaLightbulb
} from 'react-icons/fa';
import '../styles/CauseDetail.css';

// ✅ FIXED: Proper React env usage
const SALESFORCE_SITE_BASE =
  process.env.REACT_APP_SALESFORCE_SITE_BASE || '';

const SALESFORCE_DONATE_BASE =
  process.env.REACT_APP_SF_DONATION_BASE || '';

// Helpers
const splitMultiValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);

  return value
    .toString()
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
};

const ensureUrl = (value) => {
  if (!value) return '';
  const trimmed = value.toString().trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const trimmed = value.toString().trim();
    if (trimmed) return trimmed;
  }
  return '';
};

// ✅ FIXED: Correct donate URL builder
const buildDonateUrl = (cause, slug) => {
  const dtoDonateUrl = firstNonEmpty(
    cause?.donateUrl,
    cause?.Donate_URL__c
  );

  if (dtoDonateUrl) {
    if (/^https?:\/\//i.test(dtoDonateUrl)) {
      return dtoDonateUrl;
    }

    if (dtoDonateUrl.startsWith('/')) {
      return `${SALESFORCE_SITE_BASE}${dtoDonateUrl}`;
    }

    return dtoDonateUrl;
  }

  if (!SALESFORCE_DONATE_BASE) return '';

  return `${SALESFORCE_DONATE_BASE}?slug=${encodeURIComponent(slug || '')}`;
};

const CauseDetail = ({ cause, onClose }) => {
  console.log('CauseDetail cause payload:', cause);

  const name = firstNonEmpty(
    cause?.projectName,
    cause?.Project_Name__c,
    cause?.widgetName,
    cause?.name,
    'Project'
  );

  const description = firstNonEmpty(
    cause?.shortDescription,
    cause?.description,
    cause?.Description__c
  );

  const mission = firstNonEmpty(
    cause?.mission,
    cause?.Mission__c
  );

  const about = firstNonEmpty(
    cause?.about,
    cause?.About__c,
    cause?.Vision_Cause_and_Goals__c
  );

  const footerDescription = firstNonEmpty(
    cause?.footerDescription,
    cause?.Footer_Description__c
  );

  const website = ensureUrl(
    firstNonEmpty(
      cause?.website,
      cause?.Website__c,
      cause?.Website
    )
  );

  const email = firstNonEmpty(
    cause?.contactEmail,
    cause?.Contact_Email__c,
    cause?.Public_Email__c
  );

  const facebook = ensureUrl(
    firstNonEmpty(
      cause?.facebookUrl,
      cause?.Facebook__c
    )
  );

  const instagram = ensureUrl(
    firstNonEmpty(
      cause?.instagramUrl,
      cause?.Instagram__c
    )
  );

  const linkedin = ensureUrl(
    firstNonEmpty(
      cause?.linkedInUrl,
      cause?.LinkedIn__c
    )
  );

  const heroUrl = firstNonEmpty(
    cause?.heroUrl,
    cause?.Hero_URL__c
  );

  const logoUrl = firstNonEmpty(
    cause?.logoUrl,
    cause?.Logo_URL__c
  );

  const slug = firstNonEmpty(
    cause?.slug,
    cause?.Public_Slug__c
  );

  const category = firstNonEmpty(
    cause?.category,
    cause?.Category__c
  );

  const subcategories = splitMultiValue(
    cause?.subcategories ||
      cause?.subcategory ||
      cause?.Subcategory__c ||
      ''
  );

  const donateUrl = buildDonateUrl(cause, slug);

  const headerStyle = heroUrl
    ? {
        backgroundImage: `url(${heroUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {
        background:
          cause?.primaryColor ||
          cause?.Primary_Color__c ||
          '#0f172a'
      };

  const hasFocusAreas = Boolean(category || subcategories.length);
  const hasSocials = Boolean(facebook || instagram || linkedin);
  const headerText = description || mission || about || footerDescription;

  return (
    <div className="cause-detail-overlay" onClick={onClose}>
      <div
        className="cause-detail-container--modern"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <div className="cause-header--modern" style={headerStyle}>
          <div className="cause-header-inner">
            <div className="cause-header-brand">
              <div className="cause-logo-frame">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={name}
                    className="cause-logo-large"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.parentElement?.querySelector(
                        '.cause-logo-large--fallback'
                      );
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}

                <div
                  className="cause-logo-large--fallback"
                  style={{ display: logoUrl ? 'none' : 'flex' }}
                >
                  {name?.slice(0, 1)}
                </div>
              </div>

              <div className="cause-header-copy">
                <div className="cause-detail-eyebrow">
                  Fiscally Sponsored Project
                </div>
                <h2>{name}</h2>
                {headerText && <p>{headerText}</p>}

                <div className="cause-inline-actions">
                  <a href={donateUrl} className="donate-button">
                    <FaLock /> Donate Securely
                  </a>

                  {website && (
                    <a
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="website-link-button"
                    >
                      Visit Website <FaExternalLinkAlt size={12} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="cause-detail-body">
          <div className="cause-detail-main">
            {mission && (
              <section className="cause-detail-section">
                <div className="section-title">
                  <FaBullseye />
                  <h3>Mission</h3>
                </div>
                <p>{mission}</p>
              </section>
            )}

            {about && (
              <section className="cause-detail-section">
                <div className="section-title">
                  <FaLightbulb />
                  <h3>About</h3>
                </div>
                <p>{about}</p>
              </section>
            )}

            {hasFocusAreas && (
              <section className="cause-detail-section">
                <div className="section-title">
                  <FaTag />
                  <h3>Focus Areas</h3>
                </div>

                <div className="cause-tag-list">
                  {category && (
                    <span className="cause-category-chip">{category}</span>
                  )}

                  {subcategories.map((sub) => (
                    <span
                      key={sub}
                      className="cause-category-chip cause-category-chip--subtle"
                    >
                      {sub}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {footerDescription && (
              <section className="cause-detail-section">
                <div className="section-title">
                  <FaLightbulb />
                  <h3>Why This Work Matters</h3>
                </div>
                <p>{footerDescription}</p>
              </section>
            )}

            {hasSocials && (
              <section className="cause-detail-section">
                <div className="section-title">
                  <FaGlobe />
                  <h3>Follow This Project</h3>
                </div>

                <div className="cause-socials">
                  {facebook && (
                    <a href={facebook} target="_blank" rel="noreferrer" className="cause-social-link">
                      <FaFacebookF /> Facebook
                    </a>
                  )}

                  {instagram && (
                    <a href={instagram} target="_blank" rel="noreferrer" className="cause-social-link">
                      <FaInstagram /> Instagram
                    </a>
                  )}

                  {linkedin && (
                    <a href={linkedin} target="_blank" rel="noreferrer" className="cause-social-link">
                      <FaLinkedinIn /> LinkedIn
                    </a>
                  )}
                </div>
              </section>
            )}
          </div>

          <aside className="cause-detail-sidebar">
            <div className="cause-side-card highlight-card">
              <h4>Support This Cause</h4>
              <p>
                100% of your donation, minus applicable processing fees, helps
                support this charitable mission.
              </p>
              <a href={donateUrl} className="donate-button donate-button--sidebar">
                <FaLock /> Donate Now
              </a>
            </div>

            {website && (
              <div className="cause-side-card">
                <h4>Project Website</h4>
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="website-link-button"
                >
                  <FaGlobe /> Visit Website
                </a>
              </div>
            )}

            {email && (
              <div className="cause-side-card">
                <h4>Inquiries</h4>
                <a href={`mailto:${email}`} className="contact-link">
                  <FaEnvelope /> {email}
                </a>
              </div>
            )}

            <div className="cause-side-card sponsor-card">
              <h4>Fiscal Sponsorship</h4>
              <p>
                This project operates under the charitable infrastructure of
                <strong> Cause & Impact, Inc.</strong>
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CauseDetail;